// „ELFELEJTETT JELSZÓ" — PUBLIKUS oldal (nincs `requireAuth`: a felhasználó épp azért van itt, mert nem tud
// belépni). A login-oldal design-nyelvét viszi (közös `AuthShell`).
//
// ══ A LAP EGYETLEN ÉRDEMI SZABÁLYA: SOSEM ÁRULJA EL, LÉTEZIK-E A FIÓK ═══════════════════════════════════
// A BFF válasza létező és nem létező címre bájtra azonos (202 + generikus üzenet), és a UI ezt NEM próbálja
// „hasznosabbá" tenni: siker után egy megnyugtató, FELTÉTELES visszajelzést mutatunk („ha van fiók ezzel a
// címmel, elküldtük"), nem pedig „elküldtük a levelet"-et. Egy „nincs ilyen felhasználó" hibaüzenet itt
// user-enumerációt nyitna — pontosan azt, amit a platform-oldal (Odin `issued: false` ág) lezárt.
//
// A siker-állapot SZÁNDÉKOSAN nem toast, hanem a lapon MARADÓ panel: a felhasználónak most a postafiókjához
// kell mennie, és tudnia kell, hogy mit várjon (a toast eltűnik, mire odaér).
import { ForgotPasswordBodySchema } from '@atlas/contracts';
import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

type ForgotValues = z.infer<typeof ForgotPasswordBodySchema>;

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  /** A beküldés UTÁNI állapot: a lapon maradó, generikus visszajelzés (nem toast — lásd a fájl-fejlécet). */
  const [submitted, setSubmitted] = useState(false);
  const request = api.forgotPassword.useMutation();

  // Ugyanaz, mint a loginon: a mező-shape a KONTRAKTBÓL, a hibaszövegek i18n-ből (a contract-séma
  // nyelv-független, tehát a Zod angol defaultjait adta a magyar felületre is).
  const formSchema = useMemo(
    () =>
      ForgotPasswordBodySchema.extend({
        email: z
          .string()
          .min(1, t('validation:auth.emailRequired'))
          .email(t('validation:auth.emailInvalid')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit((values) => {
    request.mutate(
      { body: values },
      {
        onSuccess: () => {
          // 202 — a válasz NEM hordoz információt arról, hogy volt-e ilyen fiók. A UI sem tesz úgy.
          setSubmitted(true);
        },
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          // CSAK a tényleges üzem-zavart (429 / 503) mutatjuk hibaként — ezek NEM fiók-információk.
          // Bármi más váratlan hiba is generikus üzenetet kap (soha nem „nincs ilyen e-mail").
          toast.error(
            t(status === 429 ? 'forgotPassword.errRateLimited' : 'forgotPassword.errUnavailable'),
          );
        },
      },
    );
  });

  const backToLogin = (
    <Link
      to="/login"
      className="text-foreground inline-flex items-center gap-1.5 font-medium hover:underline"
    >
      <ArrowLeftIcon className="h-3.5 w-3.5" />
      {t('forgotPassword.backToLogin')}
    </Link>
  );

  if (submitted) {
    return (
      <AuthShell
        title={t('forgotPassword.sentTitle')}
        subtitle={t('forgotPassword.sentSubtitle')}
        footer={backToLogin}
      >
        <div
          className="bg-muted/40 flex gap-3 rounded-lg border p-4"
          role="status"
          aria-live="polite"
        >
          <EnvelopeIcon className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          <div className="space-y-1.5 text-sm">
            {/* A szöveg FELTÉTELES („ha van fiók…") — nem állítja, hogy a cím létezik. */}
            <p>{t('forgotPassword.sentBody')}</p>
            <p className="text-muted-foreground">{t('forgotPassword.sentHint')}</p>
          </div>
        </div>
        <Button variant="ghost" className="mt-4 w-full" onClick={() => setSubmitted(false)}>
          {t('forgotPassword.retry')}
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('forgotPassword.title')}
      subtitle={t('forgotPassword.subtitle')}
      footer={backToLogin}
    >
      <form onSubmit={onSubmit}>
        <div className="mb-5 space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            {t('forgotPassword.email')}
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="nev@morgens.hu"
            className="h-[38px]"
            autoFocus
            {...register('email')}
          />
          {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
        </div>

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={request.isPending}
        >
          {request.isPending ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
